import React, { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG } from '../config/api.js';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../components/LocalizedLink.js';
import { useLocalizedNavigate } from '../utils/localizedPath.js';
import '../App.css';

function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuthenticator((context) => [context.user]);
  const navigate = useLocalizedNavigate();
  const [events, setEvents] = useState([]);
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [duplicating, setDuplicating] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [unarchiving, setUnarchiving] = useState(null);
  const [permanentDeleting, setPermanentDeleting] = useState(null);
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message: string }
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'duplicate' | 'archive' | 'permanentDelete', groupId: string, eventName: string }

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000); // Auto-dismiss after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(API_CONFIG.getUrl('api/events'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      const data = await response.json();
      setEvents(data.events || []);
      setArchivedEvents(data.archivedEvents || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(t('dashboard.errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateEvent = (groupId, eventName) => {
    setConfirmAction({ type: 'duplicate', groupId, eventName });
  };

  const handleArchiveEvent = (groupId, eventName) => {
    setConfirmAction({ type: 'archive', groupId, eventName });
  };

  const handlePermanentDeleteEvent = (groupId, eventName) => {
    setConfirmAction({ type: 'permanentDelete', groupId, eventName });
  };

  const handleUnarchiveEvent = async (groupId, eventName) => {
    try {
      setUnarchiving(groupId);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(API_CONFIG.getUrl(`api/events/${groupId}/unarchive`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to unarchive event');
      }

      // Refresh the event list
      await fetchEvents();

      setNotification({ type: 'success', message: t('dashboard.success.restored', { name: eventName }) });
    } catch (err) {
      console.error('Error unarchiving event:', err);
      setNotification({ type: 'error', message: t('dashboard.errors.failedToUnarchive') });
    } finally {
      setUnarchiving(null);
    }
  };

  const confirmDuplicate = async () => {
    const { groupId } = confirmAction;
    setConfirmAction(null);

    try {
      setDuplicating(groupId);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(API_CONFIG.getUrl(`api/events/${groupId}/duplicate`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate event');
      }

      const data = await response.json();

      // Refresh the event list
      await fetchEvents();

      setNotification({ type: 'success', message: t('dashboard.success.duplicated') });
    } catch (err) {
      console.error('Error duplicating event:', err);
      setNotification({ type: 'error', message: t('dashboard.errors.failedToDuplicate') });
    } finally {
      setDuplicating(null);
    }
  };

  const confirmArchive = async () => {
    const { groupId } = confirmAction;
    setConfirmAction(null);

    try {
      setArchiving(groupId);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(API_CONFIG.getUrl(`api/events/${groupId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to archive event');
      }

      // Refresh the event list
      await fetchEvents();

      setNotification({ type: 'success', message: t('dashboard.success.archived') });
    } catch (err) {
      console.error('Error archiving event:', err);
      setNotification({ type: 'error', message: t('dashboard.errors.failedToArchive') });
    } finally {
      setArchiving(null);
    }
  };

  const confirmPermanentDelete = async () => {
    const { groupId, eventName } = confirmAction;
    setConfirmAction(null);

    try {
      setPermanentDeleting(groupId);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(API_CONFIG.getUrl(`api/events/${groupId}/permanent`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to permanently delete event');
      }

      // Refresh the event list
      await fetchEvents();

      setNotification({ type: 'success', message: t('dashboard.success.deleted', { name: eventName }) });
    } catch (err) {
      console.error('Error permanently deleting event:', err);
      setNotification({ type: 'error', message: t('dashboard.errors.failedToDelete') });
    } finally {
      setPermanentDeleting(null);
    }
  };

  const handleEventClick = (event) => {
    if (event.isPublished) {
      // View published event (read-only summary)
      navigate(`/event/${event.groupId}/summary`);
    } else {
      // Continue editing unpublished event
      navigate(`/event/${event.groupId}/edit`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('dashboard.noDateSet');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return t('dashboard.unknown');
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">{t('dashboard.title')}</h1>
          <p className="text-gray-600">{t('dashboard.welcome', { email: user?.signInDetails?.loginId })}</p>
        </div>
        <LocalizedLink to="/event-setup">
          <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition duration-200">
            {t('dashboard.createNew')}
          </button>
        </LocalizedLink>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">{t('dashboard.myEvents')}</h2>

        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">{t('dashboard.loading')}</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button
              onClick={fetchEvents}
              className="ml-4 underline hover:no-underline"
            >
              {t('dashboard.retry')}
            </button>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-gray-100 border border-gray-300 rounded-lg p-8 text-center">
            <p className="text-gray-600 mb-4">{t('dashboard.noEvents')}</p>
            <LocalizedLink to="/event-setup">
              <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                {t('dashboard.createNewEvent')}
              </button>
            </LocalizedLink>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <div
                key={event.groupId}
                className="bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden"
              >
                <div
                  onClick={() => handleEventClick(event)}
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-semibold text-gray-800 flex-1">
                      {event.name}
                    </h3>
                    {event.isPublished ? (
                      <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                        {t('dashboard.published')}
                      </span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                        {t('dashboard.draft')}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span><strong>{t('dashboard.eventDate')}</strong> {formatDate(event.eventDate)}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <span><strong>{t('dashboard.participants')}</strong> {event.participantCount}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>{t('dashboard.created')}</strong> {formatTimestamp(event.createdAt)}</span>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicateEvent(event.groupId, event.name);
                    }}
                    disabled={duplicating === event.groupId || archiving === event.groupId}
                    className="flex-1 text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {duplicating === event.groupId ? t('dashboard.duplicating') : t('dashboard.duplicate')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchiveEvent(event.groupId, event.name);
                    }}
                    disabled={archiving === event.groupId || duplicating === event.groupId}
                    className="flex-1 text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {archiving === event.groupId ? t('dashboard.archiving') : t('dashboard.archive')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Archived Events Section */}
      {archivedEvents.length > 0 && (
        <div className="mb-8">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center justify-between w-full text-left bg-gray-100 hover:bg-gray-200 rounded-lg px-4 py-3 transition-colors"
          >
            <h2 className="text-xl font-bold text-gray-700 flex items-center">
              {t('dashboard.archivedEvents', { count: archivedEvents.length })}
            </h2>
            <svg
              className={`w-5 h-5 text-gray-600 transition-transform ${showArchived ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showArchived && (
            <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {archivedEvents.map((event) => (
                <div
                  key={event.groupId}
                  className="bg-gray-50 border border-gray-300 rounded-lg shadow-md overflow-hidden opacity-75"
                >
                  <div
                    onClick={() => handleEventClick(event)}
                    className="p-6 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-semibold text-gray-700 flex-1">
                        {event.name}
                      </h3>
                      <div className="flex flex-col gap-1">
                        {event.isPublished ? (
                          <span className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                            {t('dashboard.published')}
                          </span>
                        ) : (
                          <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                            {t('dashboard.draft')}
                          </span>
                        )}
                        <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2.5 py-0.5 rounded">
                          {t('dashboard.archived')}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span><strong>{t('dashboard.eventDate')}</strong> {formatDate(event.eventDate)}</span>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <span><strong>{t('dashboard.participants')}</strong> {event.participantCount}</span>
                      </div>
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span><strong>{t('dashboard.created')}</strong> {formatTimestamp(event.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-3 bg-gray-100 border-t border-gray-300 flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnarchiveEvent(event.groupId, event.name);
                      }}
                      disabled={unarchiving === event.groupId || permanentDeleting === event.groupId}
                      className="flex-1 text-sm text-green-600 hover:text-green-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {unarchiving === event.groupId ? t('dashboard.restoring') : t('dashboard.restore')}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateEvent(event.groupId, event.name);
                      }}
                      disabled={duplicating === event.groupId || unarchiving === event.groupId || permanentDeleting === event.groupId}
                      className="flex-1 text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {duplicating === event.groupId ? t('dashboard.duplicating') : t('dashboard.duplicate')}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePermanentDeleteEvent(event.groupId, event.name);
                      }}
                      disabled={permanentDeleting === event.groupId || unarchiving === event.groupId || duplicating === event.groupId}
                      className="flex-1 text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {permanentDeleting === event.groupId ? t('dashboard.deleting') : t('dashboard.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className={`text-xl font-bold mb-4 ${
              confirmAction.type === 'permanentDelete' ? 'text-red-800' : 'text-gray-800'
            }`}>
              {confirmAction.type === 'duplicate'
                ? t('dashboard.confirmations.duplicate.title')
                : confirmAction.type === 'archive'
                  ? t('dashboard.confirmations.archive.title')
                  : t('dashboard.confirmations.permanentDelete.title')
              }
            </h3>
            <div className="text-gray-700 mb-6">
              {confirmAction.type === 'duplicate'
                ? <p>{t('dashboard.confirmations.duplicate.message', { name: confirmAction.eventName })}</p>
                : confirmAction.type === 'archive'
                  ? <p>{t('dashboard.confirmations.archive.message', { name: confirmAction.eventName })}</p>
                  : (
                      <>
                        <p className="font-bold text-red-700">{t('dashboard.confirmations.permanentDelete.warning')}</p>
                        <br />
                        <p dangerouslySetInnerHTML={{ __html: t('dashboard.confirmations.permanentDelete.message', { name: confirmAction.eventName }) }} />
                      </>
                    )
              }
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                {t('dashboard.confirmations.cancel')}
              </button>
              <button
                onClick={
                  confirmAction.type === 'duplicate'
                    ? confirmDuplicate
                    : confirmAction.type === 'archive'
                      ? confirmArchive
                      : confirmPermanentDelete
                }
                className={`${
                  confirmAction.type === 'duplicate'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-red-600 hover:bg-red-700'
                } text-white font-semibold py-2 px-6 rounded-lg transition-colors`}
              >
                {t('dashboard.confirmations.confirm', {
                  action: confirmAction.type === 'duplicate'
                    ? t('dashboard.duplicate').replace('📋 ', '')
                    : confirmAction.type === 'archive'
                      ? t('dashboard.archive').replace('🗑️ ', '')
                      : t('dashboard.delete').replace('🗑️ ', '')
                })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 ${
            notification.type === 'success' ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
          }`}>
            <div className="flex items-start">
              <div className={`flex-shrink-0 ${
                notification.type === 'success' ? 'text-green-500' : 'text-red-500'
              }`}>
                {notification.type === 'success' ? (
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.818l-2.651 3.031a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
